import { useEffect, useId, useRef, useState } from "react";
import { loadGoogleMaps } from "../earth/googleMapsLoader";
import { useLocale } from "../contexts/LocaleContext";
import "./city-autocomplete.css";

type Prediction = google.maps.places.PlacePrediction;

/** Country-scoped city suggestions; free text remains usable if Google is unavailable. */
export function CityAutocomplete({ country, value, onChange, name = "adresse", required = true }: {
  country: string; value: string; onChange: (value: string) => void; name?: string; required?: boolean;
}) {
  const { language, t } = useLocale();
  const id = useId();
  const [items, setItems] = useState<Prediction[]>([]);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(-1);
  const requestId = useRef(0);
  const token = useRef<google.maps.places.AutocompleteSessionToken | undefined>();
  const selected = useRef(value);
  const show = focused && items.length > 0;

  useEffect(() => { token.current = undefined; setItems([]); setActive(-1); }, [country, language]);
  useEffect(() => {
    const sequence = ++requestId.current;
    setItems([]); setActive(-1);
    if (!focused || !/^[A-Z]{2}$/i.test(country) || value.trim().length < 2 || value === selected.current) return;
    const timer = window.setTimeout(async () => {
      try {
        const maps = await loadGoogleMaps();
        const places = await maps.maps.importLibrary("places") as google.maps.PlacesLibrary;
        if (sequence !== requestId.current) return;
        token.current ??= new places.AutocompleteSessionToken();
        const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value.trim(), includedRegionCodes: [country.toLowerCase()], includedPrimaryTypes: ["(cities)"],
          language, sessionToken: token.current,
        });
        if (sequence === requestId.current) setItems(result.suggestions.flatMap(s => s.placePrediction ? [s.placePrediction] : []));
      } catch { /* The customer can still enter a city manually. */ }
    }, 300);
    return () => { window.clearTimeout(timer); ++requestId.current; };
  }, [value, country, language, focused]);

  async function select(item: Prediction) {
    ++requestId.current;
    token.current = undefined;
    const fallback = item.mainText?.text || item.text.text;
    selected.current = fallback;
    onChange(fallback); setItems([]); setActive(-1);
    try {
      const place = item.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });
    } catch { /* The selected suggestion's city name is already filled in. */ }

  }

  return <span className="city-autocomplete">
    <input type="text" name={name} value={value} required={required} maxLength={255} autoComplete="off"
      role="combobox" aria-autocomplete="list" aria-expanded={show} aria-controls={`${id}-options`}
      aria-activedescendant={show && active >= 0 ? `${id}-${active}` : undefined}
      onChange={e => { selected.current = ""; onChange(e.target.value); }}
      onFocus={() => setFocused(true)} onBlur={() => { setFocused(false); }}
      onKeyDown={e => {
        if (e.key === "Escape") { ++requestId.current; setItems([]); return; }
        if (!show) return;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault(); setActive(n => n < 0 ? (e.key === "ArrowDown" ? 0 : items.length - 1) : (n + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length);
        } else if (e.key === "Enter" && active >= 0) { e.preventDefault(); void select(items[active]); }
      }} />
    {show && <span className="city-suggestions">
      <span role="listbox" id={`${id}-options`} aria-label={t("register.citySuggestions")}>
        {items.map((item, index) => <span role="option" aria-selected={index === active} id={`${id}-${index}`} key={item.placeId}
          onPointerDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); void select(item); }}>
          <strong>{item.mainText?.text || item.text.text}</strong>
          {item.secondaryText && <small>{item.secondaryText.text}</small>}
        </span>)}
      </span>
      <span className="city-google-attribution" translate="no">Google Maps</span>
    </span>}
  </span>;
}
